import React, { useMemo } from 'react';
import { t } from '@sm/intl';
import { Typography } from '@wds/typography';

import { COMPARISON_PACKAGES } from '~app/pages/Pricing/lib/constants';
import { Packages, PackageDisplayNames } from '~shared/constants/pricing';
import { PackageData, PACKAGE_PRICE_TYPE } from '~shared/types/pricing/package';
import { PackageHeaderProps } from '~pricing/components/Summary/types';

import packageDefs from '~app/pages/Pricing/lib/packages/packageDefs';
import PackageCta from '~pricing/components/PackageCta';
import PackagePrice from '~app/pages/Pricing/components/PackagePrice';
import PackageByline from '~pricing/components/PackageHeader/PackageByline';
import Ribbon from '~pricing/components/Ribbon';

import COPY from './copy';

import {
  calculateSavingsPercent,
  formatMonthlyCostInLocaleCurrency,
  formatCostInLocaleCurrency,
  getCoreCostFromSkus,
} from '~app/pages/Pricing/lib/lib-helpers';

import usePackageHeaderStyles from '~app/pages/Pricing/components/PackageHeader/useStyles';
import { usePackages } from '~shared/hooks/usePackages';
import usePriceDisplay from '~pricing/hooks/usePriceDisplay';

export default function PackageHeader({
  packageName,
  comparisonSkuCosts,
  skuCosts,
  isSummaryPage = false,
}: PackageHeaderProps): React.ReactElement {
  const { Header } = usePackageHeaderStyles({ isSummaryPage });

  const comparisonPackageName = COMPARISON_PACKAGES[packageName];
  const { allPackages: packageData } = usePackages();
  const { headerCost, headerByline, headerUnit } = usePriceDisplay(packageName, skuCosts);

  const coreSeatCost = useMemo(() => getCoreCostFromSkus(skuCosts), [skuCosts]);

  const comparisonCoreSeatCost = useMemo(
    () => comparisonSkuCosts && getCoreCostFromSkus(comparisonSkuCosts),
    [comparisonSkuCosts]
  );

  const comparisonFormattedPrice = useMemo(
    () => comparisonCoreSeatCost && formatCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  const comparisonFormattedMonthlyPrice = useMemo(
    () => comparisonCoreSeatCost && formatMonthlyCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  let ribbonText = packageDefs[packageName]?.highlighted ? t(COPY.BEST_VALUE) : null;

  const comparisonPackage = useMemo(
    () =>
      comparisonPackageName
        ? packageData?.find((packageObj: PackageData) => packageObj.packageName === comparisonPackageName)
        : null,
    [comparisonPackageName, packageData]
  );

  const comparisonSeatCost = useMemo(
    () => (comparisonPackage ? getCoreCostFromSkus(comparisonPackage?.skuCost) : null),
    [comparisonPackage]
  );

  const savingsPercentage = useMemo(
    () => (coreSeatCost && comparisonSeatCost ? calculateSavingsPercent(comparisonSeatCost, coreSeatCost) : 0),
    [comparisonSeatCost, coreSeatCost]
  );

  if (savingsPercentage > 0) {
    ribbonText = t(COPY.SAVE_PERCENTAGE, { percentage: savingsPercentage });
  }

  return (
    <>
      {ribbonText && <Ribbon packageName={packageName}>{ribbonText}</Ribbon>}

      <header data-testid="PackageHeader__Header" className={Header}>
        <Typography component="h2" data-testid="PackageHeader__HeaderText" align="center">
          {PackageDisplayNames[packageName]}
        </Typography>
        {packageName !== Packages.Enterprise && (
          <PackagePrice
            packagePriceType={isSummaryPage ? PACKAGE_PRICE_TYPE.SUMMARY : PACKAGE_PRICE_TYPE.DETAIL}
            costUnit={headerUnit}
            formattedPrice={headerCost}
          />
        )}
        {comparisonFormattedPrice && (
          <PackagePrice
            packagePriceType={PACKAGE_PRICE_TYPE.COMPARISON}
            formattedPrice={
              packageName === Packages.StandardMonthly ? comparisonFormattedPrice : comparisonFormattedMonthlyPrice
            }
          />
        )}
        <PackageByline byline={headerByline} />
        <PackageCta packageName={packageName} isSummaryPage={isSummaryPage} />
      </header>
    </>
  );
}
